// Generated from research notes (scratchpad ev/shares-*.json). Edit the data, not the shape.
import type { Citation } from '@/domain/analytics/citations';

export interface MuscleShare {
  /** Display name of the muscle or region. */
  region: string;
  /** Approximate share of the work, in percent. The shares of an exercise add up to 100. */
  share: number;
}

export interface ExerciseShares {
  regions: MuscleShare[];
  /** moderate: a study of this exercise or a near-identical one; low: from the closest studied exercise. */
  confidence: 'moderate' | 'low';
  sources: string[];
  note: string;
}

/** Studies behind the shares. */
export const SHARE_SOURCES: Record<string, Citation> = {
  andersen2021: {
    id: 'andersen2021',
    authors:
      'Andersen V, Pedersen H, Fimland MS, Shaw M, Solstad TEJ, Stien N, Cumming KT, Saeterbakken AH',
    year: 2021,
    title:
      'Comparison of muscle activity in three single-joint, hip extension exercises in resistance-trained women',
    journal: 'Journal of Sports Science and Medicine',
    doi: '10.52082/jssm.2021.181',
    openAccess: false,
  },
  bourne2017: {
    id: 'bourne2017',
    authors:
      'Bourne MN, Duhig SJ, Timmins RG, Williams MD, Opar DA, Al Najjar A, Kerr GK, Shield AJ',
    year: 2017,
    title:
      'Impact of the Nordic hamstring and hip extension exercises on hamstring architecture and morphology: implications for injury prevention',
    journal: 'British Journal of Sports Medicine',
    doi: '10.1136/bjsports-2016-096130',
    openAccess: false,
  },
  calatayud2015: {
    id: 'calatayud2015',
    authors: 'Calatayud J, Borreani S, Colado JC, Martin F, Tella V, Andersen LL',
    year: 2015,
    title:
      'Bench press and push-up at comparable levels of muscle activity results in similar strength gains',
    journal: 'Journal of Strength and Conditioning Research',
    doi: '10.1519/JSC.0000000000000589',
    openAccess: false,
  },
  campos2020: {
    id: 'campos2020',
    authors:
      'Campos YAC, Vianna JM, Guimarães MP, Oliveira JLD, Hernández-Mosqueira C, da Silva SF, Marchetti PH',
    year: 2020,
    title:
      'Different Shoulder Exercises Affect the Activation of Deltoid Portions in Resistance-Trained Individuals',
    journal: 'Journal of Human Kinetics',
    doi: '10.2478/hukin-2020-0033',
    openAccess: false,
  },
  contreras2015: {
    id: 'contreras2015',
    authors: 'Contreras B, Vigotsky AD, Schoenfeld BJ, Beardsley C, Cronin J',
    year: 2015,
    title:
      'A comparison of gluteus maximus, biceps femoris, and vastus lateralis electromyographic activity in the back squat and barbell hip thrust exercises',
    journal: 'Journal of Applied Biomechanics',
    doi: '10.1123/jab.2014-0301',
    openAccess: false,
  },
  coratella2020bench: {
    id: 'coratella2020bench',
    authors: 'Coratella G, Tornatore G, Longo S, Esposito F, Cè E',
    year: 2020,
    title:
      "Specific prime movers' excitation during free-weight bench press variations and chest press machine in competitive bodybuilders",
    journal: 'European Journal of Sport Science',
    doi: '10.1080/17461391.2019.1655101',
    openAccess: false,
  },
  coratella2020raise: {
    id: 'coratella2020raise',
    authors: 'Coratella G, Tornatore G, Longo S, Esposito F, Cè E',
    year: 2020,
    title:
      'An Electromyographic Analysis of Lateral Raise Variations and Frontal Raise in Competitive Bodybuilders',
    journal: 'International Journal of Environmental Research and Public Health',
    doi: '10.3390/ijerph17176015',
    openAccess: false,
  },
  coratella2022ohp: {
    id: 'coratella2022ohp',
    authors: 'Coratella G, Tornatore G, Longo S, Esposito F, Cè E',
    year: 2022,
    title:
      'Front vs Back and Barbell vs Machine Overhead Press: An Electromyographic Analysis and Implications For Resistance Training',
    journal: 'Frontiers in Physiology',
    doi: '10.3389/fphys.2022.825880',
    openAccess: false,
  },
  coratella2023: {
    id: 'coratella2023',
    authors: 'Coratella G, Tornatore G, Longo S, Toninelli N, Padovan R, Esposito F, Cè E',
    year: 2023,
    title:
      'Biceps brachii and brachioradialis excitation in biceps curl exercise: different handgrips, different synergy',
    journal: 'Sports',
    doi: '10.3390/sports11030064',
    openAccess: false,
  },
  distefano2009: {
    id: 'distefano2009',
    authors: 'Distefano LJ, Blackburn JT, Marshall SW, Padua DA',
    year: 2009,
    title: 'Gluteal muscle activation during common therapeutic exercises',
    journal: 'Journal of Orthopaedic and Sports Physical Therapy',
    doi: '10.2519/jospt.2009.2796',
    openAccess: false,
  },
  ema2013: {
    id: 'ema2013',
    authors: 'Ema R, Wakahara T, Miyamoto N, Kanehisa H, Kawakami Y',
    year: 2013,
    title:
      'Inhomogeneous architectural changes of the quadriceps femoris induced by resistance training',
    journal: 'European Journal of Applied Physiology',
    url: 'https://pubmed.ncbi.nlm.nih.gov/23949789/',
    openAccess: false,
  },
  escamilla2001: {
    id: 'escamilla2001',
    authors: 'Escamilla RF, Fleisig GS, Zheng N, et al.',
    year: 2001,
    title: 'Effects of technique variations on knee biomechanics during the squat and leg press',
    journal: 'Medicine and Science in Sports and Exercise',
    url: 'https://pubmed.ncbi.nlm.nih.gov/11528346/',
    openAccess: false,
  },
  escamilla2002: {
    id: 'escamilla2002',
    authors: 'Escamilla RF, Francisco AC, Kayes AV, Speer KP, Moorman CT',
    year: 2002,
    title: 'An electromyographic analysis of sumo and conventional style deadlifts',
    journal: 'Medicine and Science in Sports and Exercise',
    url: 'https://scholars.csus.edu/esploro/outputs/journalArticle/An-electromyographic-analysis-of-sumo-and/99257880236901671',
    openAccess: false,
  },
  escamilla2006: {
    id: 'escamilla2006',
    authors:
      "Escamilla RF, Babb E, DeWitt R, Jew P, Kelleher P, Burnham T, Busch J, D'Anna K, Mowbray R, Imamura RT",
    year: 2006,
    title:
      'Electromyographic analysis of traditional and nontraditional abdominal exercises: implications for rehabilitation and training',
    journal: 'Physical Therapy',
    doi: '10.1093/ptj/86.5.656',
    openAccess: false,
  },
  escamilla2010: {
    id: 'escamilla2010',
    authors: 'Escamilla RF et al.',
    year: 2010,
    title: 'Core muscle activation during Swiss ball and traditional abdominal exercises',
    journal: 'Journal of Orthopaedic and Sports Physical Therapy',
    doi: '10.2519/jospt.2010.3073',
    openAccess: false,
  },
  fenwick2009: {
    id: 'fenwick2009',
    authors: 'Fenwick CMJ, Brown SHM, McGill SM',
    year: 2009,
    title:
      'Comparison of different rowing exercises: trunk muscle activation and lumbar spine motion, load, and stiffness',
    journal: 'Journal of Strength and Conditioning Research',
    url: 'https://pubmed.ncbi.nlm.nih.gov/19197209/',
    openAccess: false,
  },
  kinoshita2023: {
    id: 'kinoshita2023',
    authors: 'Kinoshita M, Maeo S, Kobayashi Y, et al.',
    year: 2023,
    title:
      'Triceps surae muscle hypertrophy is greater after standing versus seated calf-raise training',
    journal: 'Frontiers in Physiology',
    doi: '10.3389/fphys.2023.1272106',
    openAccess: false,
  },
  kleiber2015: {
    id: 'kleiber2015',
    authors: 'Kleiber T, Kunz L, Disselhorst-Klug C',
    year: 2015,
    title:
      'Muscular coordination of biceps brachii and brachioradialis in elbow flexion with respect to hand position',
    journal: 'Frontiers in Physiology',
    doi: '10.3389/fphys.2015.00215',
    openAccess: false,
  },
  mcbride2010: {
    id: 'mcbride2010',
    authors: 'McBride JM, Skinner JW, Schafer PC, Haines TL, Kirby TJ',
    year: 2010,
    title: 'Comparison of kinetic variables and muscle activity during a squat vs. a box squat',
    journal: 'Journal of Strength and Conditioning Research',
    url: 'https://pubmed.ncbi.nlm.nih.gov/?term=Comparison+of+kinetic+variables+and+muscle+activity+during+a+squat+vs.+a+box+squat',
    openAccess: false,
  },
  kubo2019: {
    id: 'kubo2019',
    authors: 'Kubo K, Ikebukuro T, Yata H',
    year: 2019,
    title: 'Effects of squat training with different depths on lower limb muscle volumes',
    journal: 'European Journal of Applied Physiology',
    doi: '10.1007/s00421-019-04181-y',
    openAccess: false,
  },
  lauver2016: {
    id: 'lauver2016',
    authors: 'Lauver JD, Cayot TE, Scheuermann BW',
    year: 2016,
    title:
      'Influence of bench angle on upper extremity muscular activation during bench press exercise',
    journal: 'European Journal of Sport Science',
    doi: '10.1080/17461391.2015.1022605',
    openAccess: false,
  },
  lehman2004: {
    id: 'lehman2004',
    authors: 'Lehman GJ et al.',
    year: 2004,
    title:
      'Variations in muscle activation levels during traditional latissimus dorsi weight training exercises: an experimental study',
    journal: 'Dynamic Medicine',
    doi: '10.1186/1476-5918-3-4',
    openAccess: false,
  },
  lopezVivancos2023: {
    id: 'lopezVivancos2023',
    authors: 'López-Vivancos A, González-Gálvez N, Orquín-Castrillón FJ, Vale RGS, Marcos-Pardo PJ',
    year: 2023,
    title:
      'Electromyographic Activity of the Pectoralis Major Muscle during Traditional Bench Press and Other Variants of Pectoral Exercises: A Systematic Review and Meta-Analysis',
    journal: 'Applied Sciences 13(8):5203',
    doi: '10.3390/app13085203',
    openAccess: false,
  },
  lusk2010: {
    id: 'lusk2010',
    authors: 'Lusk SJ, Hale BD, Russell DM',
    year: 2010,
    title: 'Grip width and forearm orientation effects on muscle activity during the lat pull-down',
    journal: 'Journal of Strength and Conditioning Research',
    doi: '10.1519/JSC.0b013e3181ddb0ab',
    openAccess: false,
  },
  mackey2021: {
    id: 'mackey2021',
    authors: 'Mackey ER, Riemann BL',
    year: 2021,
    title: 'Biomechanical differences between the Bulgarian split-squat and back squat',
    journal: 'International Journal of Exercise Science',
    doi: '10.70252/CIYT8956',
    openAccess: false,
  },
  maeo2021: {
    id: 'maeo2021',
    authors: 'Maeo S, Huang M, Wu Y, et al.',
    year: 2021,
    title:
      'Greater hamstrings muscle hypertrophy but similar damage protection after training at long versus short muscle lengths',
    journal: 'Medicine and Science in Sports and Exercise',
    doi: '10.1249/MSS.0000000000002523',
    openAccess: false,
  },
  maeo2023: {
    id: 'maeo2023',
    authors: 'Maeo S, Wu Y, Huang M, Sakurai H, Kusagawa Y, Sugiyama T, Kanehisa H, Isaka T',
    year: 2023,
    title:
      'Triceps brachii hypertrophy is substantially greater after elbow extension training performed in the overhead versus neutral arm position',
    journal: 'European Journal of Sport Science',
    doi: '10.1080/17461391.2022.2100279',
    openAccess: false,
  },
  marcolin2018: {
    id: 'marcolin2018',
    authors: 'Marcolin G et al.',
    year: 2018,
    title:
      'Differences in electromyographic activity of biceps brachii and brachioradialis while performing three variants of curl',
    journal: 'PeerJ',
    doi: '10.7717/peerj.5165',
    openAccess: false,
  },
  martinfuentes2020: {
    id: 'martinfuentes2020',
    authors: 'Martín-Fuentes I, Oliva-Lozano JM, Muyor JM',
    year: 2020,
    title: 'Electromyographic activity in deadlift exercise and its variants. A systematic review',
    journal: 'PLOS ONE',
    doi: '10.1371/journal.pone.0229507',
    openAccess: false,
  },
  mcallister2013: {
    id: 'mcallister2013',
    authors: 'McAllister MJ, et al.',
    year: 2013,
    title: 'Effect of grip width on electromyographic activity during the upright row',
    journal: 'Journal of Strength and Conditioning Research',
    doi: '10.1519/JSC.0b013e31824f23ad',
    openAccess: false,
  },
  mcallister2014: {
    id: 'mcallister2014',
    authors: 'McAllister MJ, Hammond KG, Schilling BK, et al.',
    year: 2014,
    title: 'Muscle activation during various hamstring exercises',
    journal: 'Journal of Strength and Conditioning Research',
    doi: '10.1519/JSC.0000000000000302',
    openAccess: false,
  },
  mcgill2012: {
    id: 'mcgill2012',
    authors: 'McGill SM, Marshall LW',
    year: 2012,
    title:
      'Kettlebell swing, snatch, and bottoms-up carry: back and hip muscle activation, motion, and low back loads',
    journal: 'Journal of Strength and Conditioning Research',
    url: 'https://pubmed.ncbi.nlm.nih.gov/21997449/',
    openAccess: false,
  },
  mckenzie2022dip: {
    id: 'mckenzie2022dip',
    authors: 'McKenzie A, Crowley-McHattan Z, Meir R, Whitting J, Volschenk W',
    year: 2022,
    title:
      'Fatigue Increases Muscle Activations but Does Not Change Maximal Joint Angles during the Bar Dip',
    journal: 'International Journal of Environmental Research and Public Health',
    doi: '10.3390/ijerph192114390',
    openAccess: false,
  },
  muyor2020: {
    id: 'muyor2020',
    authors: 'Muyor JM, Martín-Fuentes I, Rodríguez-Ridao D, Antequera-Vique JA',
    year: 2020,
    title:
      'Electromyographic activity in the gluteus medius, gluteus maximus, biceps femoris, vastus lateralis, vastus medialis and rectus femoris during the monopodal squat, forward lunge and lateral step-up exercises',
    journal: 'PLOS ONE',
    url: 'https://pmc.ncbi.nlm.nih.gov/articles/PMC7112217/',
    openAccess: false,
  },
  muyor2022: {
    id: 'muyor2022',
    authors: 'Muyor JM, López-Miñarro PA, Alacid F',
    year: 2022,
    title:
      'Comparison of electromyographic activity during barbell pullover and straight arm pulldown exercises',
    journal: 'Applied Sciences',
    doi: '10.3390/app122111138',
    openAccess: false,
  },
  neto2020: {
    id: 'neto2020',
    authors: 'Krause Neto W, Soares EG, Vieira TL, Aguiar R, Chola TA, Sampaio VL, Gama EF',
    year: 2020,
    title:
      'Gluteus maximus activation during common strength and hypertrophy exercises: a systematic review',
    journal: 'Journal of Sports Science and Medicine',
    url: 'https://pmc.ncbi.nlm.nih.gov/articles/PMC7039033/',
    openAccess: false,
  },
  oliveira2009: {
    id: 'oliveira2009',
    authors: 'Oliveira LF, Matta TT, Alves DS, Garcia MAC, Vieira TMM',
    year: 2009,
    title: 'Effect of the shoulder position on the biceps brachii EMG in different dumbbell curls',
    journal: 'Journal of Sports Science and Medicine',
    url: 'https://jssm.org/jssm-08-24.xml-abst',
    openAccess: false,
  },
  page2012: {
    id: 'page2012',
    authors: 'Pagé I, Descarreaux M',
    year: 2012,
    title: 'Trunk muscle fatigue during a lateral isometric hold test: what are we evaluating?',
    journal: 'Chiropractic & Manual Therapies',
    doi: '10.1186/2045-709X-20-12',
    openAccess: false,
  },
  padovan2025: {
    id: 'padovan2025',
    authors: 'Padovan R, Toninelli N, Longo S, Tornatore G, Esposito F, Cè E, Coratella G',
    year: 2025,
    title:
      'High-Density Surface Electromyography Excitation in Front vs. Back Overhead Press Prime Movers',
    journal: 'Journal of Human Kinetics',
    doi: '10.5114/jhk/205466',
    openAccess: true,
  },
  pizzari2014: {
    id: 'pizzari2014',
    authors: 'Pizzari T, Wickham J, Balster S, Ganderton C, Watson L',
    year: 2014,
    title: 'Modifying a shrug exercise can facilitate the upward rotator muscles of the scapula',
    journal: 'Clinical Biomechanics',
    doi: '10.1016/j.clinbiomech.2013.11.011',
    openAccess: false,
  },
  rodriguezRidao2020: {
    id: 'rodriguezRidao2020',
    authors: 'Rodríguez-Ridao D, Antequera-Vique JA, Martín-Fuentes I, Muyor JM',
    year: 2020,
    title:
      'Effect of Five Bench Inclinations on the Electromyographic Activity of the Pectoralis Major, Anterior Deltoid, and Triceps Brachii during the Bench Press Exercise',
    journal: 'International Journal of Environmental Research and Public Health',
    doi: '10.3390/ijerph17197339',
    openAccess: false,
  },
  saeterbakken2011: {
    id: 'saeterbakken2011',
    authors: 'Saeterbakken AH, van den Tillaar R, Fimland MS',
    year: 2011,
    title:
      'A comparison of muscle activity and 1-RM strength of three chest-press exercises with different stability requirements',
    journal: 'Journal of Sports Sciences',
    url: 'https://app.cristin.no/results/show.jsf?id=741530',
    openAccess: false,
  },
  schoenfeld2010: {
    id: 'schoenfeld2010',
    authors: 'Schoenfeld BJ',
    year: 2010,
    title: 'Squatting kinematics and kinetics and their application to exercise performance',
    journal: 'Journal of Strength and Conditioning Research',
    url: 'https://pubmed.ncbi.nlm.nih.gov/20182386/',
    openAccess: false,
  },
  schoenfeld2013: {
    id: 'schoenfeld2013',
    authors: 'Schoenfeld B, Sonmez RGT, Kolber MJ, Contreras B, Harris R, Ozen S',
    year: 2013,
    title:
      'Effect of hand position on EMG activity of the posterior shoulder musculature during a horizontal abduction exercise',
    journal: 'Journal of Strength and Conditioning Research',
    doi: '10.1519/JSC.0b013e318281e1e9',
    openAccess: false,
  },
  schoenfeld2014: {
    id: 'schoenfeld2014',
    authors: 'Schoenfeld BJ, Contreras B, Tiryaki-Sonmez G, Willardson JM, Fontana F',
    year: 2014,
    title:
      'An electromyographic comparison of a modified version of the plank with a long lever and posterior tilt versus the traditional plank exercise',
    journal: 'Sports Biomechanics',
    doi: '10.1080/14763141.2014.942355',
    openAccess: false,
  },
  serner2014: {
    id: 'serner2014',
    authors: 'Serner A, Jakobsen MD, Andersen LL, Hölmich P, Sundstrup E, Thorborg K',
    year: 2014,
    title:
      'EMG evaluation of hip adduction exercises for soccer players: implications for exercise selection in prevention and treatment of groin injuries',
    journal: 'British Journal of Sports Medicine',
    doi: '10.1136/bjsports-2012-091746',
    openAccess: false,
  },
  solstad2020: {
    id: 'solstad2020',
    authors: 'Solstad TEJ, Andersen V, Shaw M, Hoel EM, Vonheim A, Saeterbakken AH',
    year: 2020,
    title:
      'A Comparison of Muscle Activation between Barbell Bench Press and Dumbbell Flyes in Resistance-Trained Males',
    journal: 'Journal of Sports Science and Medicine',
    url: 'https://jssm.org/jssm-19-645.xml-abst',
    openAccess: false,
  },
  stephens2021: {
    id: 'stephens2021',
    authors: 'Stephens J, Bacon E, Evans C, Locke S, McCulloch R',
    year: 2021,
    title:
      'Anti-rotational and rotational abdominal exercises and the concurrent muscle activation: a methodology study',
    journal: 'International Journal of Exercise Science: Conference Proceedings',
    url: 'https://digitalcommons.wku.edu/ijesab/vol8/iss9/12',
    openAccess: false,
  },
  vasconcelos2023: {
    id: 'vasconcelos2023',
    authors: 'Vasconcelos CMWA, Lopes CR, Almeida VM, Krause Neto W, Soares E',
    year: 2023,
    title:
      'Effect of different grip position and shoulder-abduction angle on muscle strength and activation during the seated cable row',
    journal: 'International Journal of Strength and Conditioning',
    url: 'https://doaj.org/article/0fdda36a614441939af8e50196fcb4cb',
    openAccess: false,
  },
  vigotsky2015: {
    id: 'vigotsky2015',
    authors: 'Vigotsky AD, Harper EN, Ryan DR, Contreras B',
    year: 2015,
    title: 'Effects of load on good morning kinematics and EMG activity',
    journal: 'PeerJ',
    doi: '10.7717/peerj.708',
    openAccess: false,
  },
  yavuz2015: {
    id: 'yavuz2015',
    authors: 'Yavuz HU, Erdag D, Amca AM, Aritan S',
    year: 2015,
    title: 'Kinematic and EMG activities during front and back squat variations in maximum loads',
    journal: 'Journal of Sports Sciences',
    url: 'https://www.bisp-surf.de/Record/PU201505003639',
    openAccess: false,
  },
  youdas2010: {
    id: 'youdas2010',
    authors: 'Youdas JW, Amundson CL, Cicero KS, Hahn JJ, Harezlak DT, Hollman JH',
    year: 2010,
    title:
      'Surface electromyographic activation patterns and elbow joint motion during a pull-up, chin-up, or perfect-pullup rotational exercise',
    journal: 'Journal of Strength and Conditioning Research',
    doi: '10.1519/JSC.0b013e3181f1598c',
    openAccess: false,
  },
};

/** By exercise key. Cardio and anything without a meaningful split is absent. */
export const MUSCLE_SHARES: Record<string, ExerciseShares> = {
  'ab-wheel-rollout': {
    regions: [
      { region: 'Upper abs', share: 30 },
      { region: 'Lower abs', share: 25 },
      { region: 'Obliques', share: 20 },
      { region: 'Lats', share: 15 },
      { region: 'Hip flexors', share: 10 },
    ],
    confidence: 'moderate',
    sources: ['escamilla2006', 'escamilla2010'],
    note: 'Roll-outs gave some of the highest rectus abdominis and oblique activity, along with high lat activity.',
  },
  'arnold-press': {
    regions: [
      { region: 'Front delts', share: 45 },
      { region: 'Side delts', share: 25 },
      { region: 'Triceps', share: 20 },
      { region: 'Upper back and traps', share: 10 },
    ],
    confidence: 'low',
    sources: ['campos2020', 'coratella2022ohp'],
    note: 'Extrapolated from shoulder press data; the rotation starts with palms in, which keeps the front delts busy.',
  },
  'assisted-pull-up': {
    regions: [
      { region: 'Lats', share: 45 },
      { region: 'Mid back', share: 20 },
      { region: 'Biceps', share: 15 },
      { region: 'Forearms (brachioradialis)', share: 10 },
      { region: 'Grip', share: 10 },
    ],
    confidence: 'low',
    sources: ['youdas2010'],
    note: 'Extrapolated from the unassisted pull-up; assistance lowers the load but not the pattern.',
  },
  'back-extension': {
    regions: [
      { region: 'Lower back', share: 45 },
      { region: 'Hamstrings', share: 30 },
      { region: 'Glutes', share: 25 },
    ],
    confidence: 'moderate',
    sources: ['andersen2021'],
    note: 'The 45 degree Roman chair worked the erectors as much as a Romanian deadlift and the hamstrings and glutes more.',
  },
  'back-squat': {
    regions: [
      { region: 'Quads', share: 45 },
      { region: 'Glutes', share: 25 },
      { region: 'Adductors', share: 20 },
      { region: 'Lower back', share: 10 },
    ],
    confidence: 'moderate',
    sources: ['kubo2019', 'contreras2015', 'schoenfeld2010'],
    note: 'Deep squats grew the quads, glutes and adductors on MRI while the hamstrings did not change, so hamstrings are left out.',
  },
  'band-pull-apart': {
    regions: [
      { region: 'Rear delts', share: 45 },
      { region: 'Upper back and traps', share: 45 },
      { region: 'Side delts', share: 10 },
    ],
    confidence: 'low',
    sources: ['schoenfeld2013'],
    note: 'Extrapolated from reverse fly data: pulling the band apart works the rear delts and upper back together.',
  },
  'barbell-bench-press': {
    regions: [
      { region: 'Mid chest', share: 35 },
      { region: 'Triceps', share: 20 },
      { region: 'Lower chest', share: 15 },
      { region: 'Upper chest', share: 15 },
      { region: 'Front delts', share: 15 },
    ],
    confidence: 'moderate',
    sources: ['rodriguezRidao2020', 'coratella2020bench'],
    note: 'On a flat bench all three parts of the chest work about equally hard, with the sternal (mid) chest doing the most and the front delts and triceps helping.',
  },
  'barbell-curl': {
    regions: [
      { region: 'Biceps', share: 55 },
      { region: 'Brachialis', share: 30 },
      { region: 'Forearms (brachioradialis)', share: 15 },
    ],
    confidence: 'moderate',
    sources: ['marcolin2018', 'coratella2023'],
    note: 'A supinated grip gives the highest biceps activity; brachialis share is estimated because surface EMG cannot reach it.',
  },
  'barbell-row': {
    regions: [
      { region: 'Mid back', share: 30 },
      { region: 'Lats', share: 25 },
      { region: 'Lower back', share: 20 },
      { region: 'Biceps', share: 15 },
      { region: 'Rear delts', share: 10 },
    ],
    confidence: 'moderate',
    sources: ['fenwick2009'],
    note: 'The bent-over row showed high lat and thoracic erector activity plus the highest lumbar erector load of the rows tested.',
  },
  'barbell-shrug': {
    regions: [
      { region: 'Upper traps', share: 80 },
      { region: 'Mid back', share: 10 },
      { region: 'Grip', share: 10 },
    ],
    confidence: 'moderate',
    sources: ['pizzari2014'],
    note: 'Shrugs mainly elevate the scapula through the upper trapezius, with smaller middle trapezius and grip contributions.',
  },
  'bench-dip': {
    regions: [
      { region: 'Triceps', share: 55 },
      { region: 'Front delts', share: 25 },
      { region: 'Lower chest', share: 20 },
    ],
    confidence: 'low',
    sources: ['mckenzie2022dip'],
    note: 'Extrapolated from bar dip data: with feet supported and hands behind you, more of the work falls on the triceps.',
  },
  'bulgarian-split-squat': {
    regions: [
      { region: 'Quads', share: 45 },
      { region: 'Glutes', share: 35 },
      { region: 'Adductors', share: 10 },
      { region: 'Hamstrings', share: 10 },
    ],
    confidence: 'moderate',
    sources: ['mackey2021', 'neto2020'],
    note: 'Compared with the back squat it asks less of the knee and is hip dominant, so the glutes take a larger share.',
  },
  'cable-crunch': {
    regions: [
      { region: 'Upper abs', share: 45 },
      { region: 'Lower abs', share: 30 },
      { region: 'Obliques', share: 20 },
      { region: 'Hip flexors', share: 5 },
    ],
    confidence: 'low',
    sources: ['escamilla2006', 'escamilla2010'],
    note: 'Extrapolated from the floor crunch; kneeling with a cable adds load without changing which muscles flex the spine.',
  },
  'cable-curl': {
    regions: [
      { region: 'Biceps', share: 55 },
      { region: 'Brachialis', share: 30 },
      { region: 'Forearms (brachioradialis)', share: 15 },
    ],
    confidence: 'moderate',
    sources: ['coratella2023'],
    note: 'Underhand cable curls produced the highest biceps and brachioradialis activity of the grips tested.',
  },
  'cable-fly': {
    regions: [
      { region: 'Mid chest', share: 50 },
      { region: 'Lower chest', share: 20 },
      { region: 'Upper chest', share: 15 },
      { region: 'Front delts', share: 15 },
    ],
    confidence: 'low',
    sources: ['solstad2020'],
    note: 'Extrapolated from dumbbell fly data: a mid-height cable fly is mostly chest with a little front delt and no triceps; raising or lowering the cables shifts work.',
  },
  'cable-kickback': {
    regions: [
      { region: 'Glutes', share: 80 },
      { region: 'Hamstrings', share: 20 },
    ],
    confidence: 'low',
    sources: ['neto2020'],
    note: 'An isolated hip extension where the glutes do most of the work; extrapolated from glute activation review data.',
  },
  'cable-lateral-raise': {
    regions: [
      { region: 'Side delts', share: 50 },
      { region: 'Upper back and traps', share: 20 },
      { region: 'Front delts', share: 15 },
      { region: 'Rear delts', share: 15 },
    ],
    confidence: 'low',
    sources: ['coratella2020raise'],
    note: 'Extrapolated from dumbbell lateral raise data; the cable keeps tension at the bottom but works the same muscles.',
  },
  'chest-supported-row': {
    regions: [
      { region: 'Mid back', share: 35 },
      { region: 'Lats', share: 30 },
      { region: 'Biceps', share: 20 },
      { region: 'Rear delts', share: 15 },
    ],
    confidence: 'low',
    sources: ['vasconcelos2023', 'lehman2004'],
    note: 'Extrapolated from seated row studies; chest support removes lower back work, leaving upper back, lats and arms.',
  },
  'chin-up': {
    regions: [
      { region: 'Lats', share: 45 },
      { region: 'Biceps', share: 25 },
      { region: 'Mid back', share: 15 },
      { region: 'Brachialis', share: 10 },
      { region: 'Grip', share: 5 },
    ],
    confidence: 'moderate',
    sources: ['youdas2010'],
    note: 'The underhand grip keeps lat activity the same as a pull-up but raises biceps (and chest) activity.',
  },
  'close-grip-bench-press': {
    regions: [
      { region: 'Triceps', share: 40 },
      { region: 'Mid chest', share: 30 },
      { region: 'Front delts', share: 20 },
      { region: 'Upper chest', share: 10 },
    ],
    confidence: 'low',
    sources: ['rodriguezRidao2020'],
    note: 'Extrapolated from flat bench press data: a narrower grip moves more of the work to the triceps.',
  },
  'close-grip-lat-pulldown': {
    regions: [
      { region: 'Lats', share: 45 },
      { region: 'Biceps', share: 25 },
      { region: 'Mid back', share: 20 },
      { region: 'Rear delts', share: 10 },
    ],
    confidence: 'moderate',
    sources: ['lusk2010'],
    note: 'Narrow grips kept lat activity close to the wide grip; a little more elbow flexion shifts some work to the biceps.',
  },
  crunch: {
    regions: [
      { region: 'Upper abs', share: 50 },
      { region: 'Lower abs', share: 30 },
      { region: 'Obliques', share: 20 },
    ],
    confidence: 'moderate',
    sources: ['escamilla2006', 'escamilla2010'],
    note: 'The crunch works the rectus abdominis with modest oblique activity and little hip flexor activity.',
  },
  deadlift: {
    regions: [
      { region: 'Lower back', share: 35 },
      { region: 'Glutes', share: 25 },
      { region: 'Hamstrings', share: 20 },
      { region: 'Upper traps', share: 10 },
      { region: 'Grip', share: 10 },
    ],
    confidence: 'moderate',
    sources: ['martinfuentes2020'],
    note: 'The review finds erector spinae, gluteus maximus and hamstrings among the most active muscles; quads also work hard but are not a tracked region here.',
  },
  'decline-bench-press': {
    regions: [
      { region: 'Mid chest', share: 40 },
      { region: 'Lower chest', share: 25 },
      { region: 'Triceps', share: 20 },
      { region: 'Front delts', share: 10 },
      { region: 'Upper chest', share: 5 },
    ],
    confidence: 'low',
    sources: ['lopezVivancos2023', 'rodriguezRidao2020'],
    note: 'A decline shifts work away from the upper chest and toward the sternal (mid and lower) chest; shares follow the flat bench with that shift.',
  },
  'decline-dumbbell-press': {
    regions: [
      { region: 'Mid chest', share: 40 },
      { region: 'Lower chest', share: 25 },
      { region: 'Triceps', share: 20 },
      { region: 'Front delts', share: 10 },
      { region: 'Upper chest', share: 5 },
    ],
    confidence: 'low',
    sources: ['lopezVivancos2023', 'rodriguezRidao2020'],
    note: 'A decline shifts work away from the upper chest and toward the sternal (mid and lower) chest; shares follow the flat bench with that shift.',
  },
  'decline-sit-up': {
    regions: [
      { region: 'Upper abs', share: 30 },
      { region: 'Hip flexors', share: 30 },
      { region: 'Lower abs', share: 25 },
      { region: 'Obliques', share: 15 },
    ],
    confidence: 'low',
    sources: ['escamilla2010', 'escamilla2006'],
    note: 'Extrapolated from the flat sit-up, which matched the crunch for abs but used the hip flexors far more.',
  },
  dips: {
    regions: [
      { region: 'Lower chest', share: 35 },
      { region: 'Triceps', share: 30 },
      { region: 'Front delts', share: 15 },
      { region: 'Mid chest', share: 10 },
      { region: 'Serratus', share: 10 },
    ],
    confidence: 'moderate',
    sources: ['mckenzie2022dip'],
    note: 'Bar dips work the chest and triceps hardest, with front delts and the shoulder blade muscles stabilising.',
  },
  'dumbbell-bench-press': {
    regions: [
      { region: 'Mid chest', share: 40 },
      { region: 'Lower chest', share: 15 },
      { region: 'Upper chest', share: 15 },
      { region: 'Front delts', share: 15 },
      { region: 'Triceps', share: 15 },
    ],
    confidence: 'moderate',
    sources: ['saeterbakken2011', 'rodriguezRidao2020'],
    note: 'Chest and front delt activity match the barbell bench press, with slightly less triceps involvement.',
  },
  'dumbbell-curl': {
    regions: [
      { region: 'Biceps', share: 55 },
      { region: 'Brachialis', share: 30 },
      { region: 'Forearms (brachioradialis)', share: 15 },
    ],
    confidence: 'moderate',
    sources: ['marcolin2018', 'oliveira2009'],
    note: 'Dumbbell curls train the same elbow flexors as the barbell, with slightly lower overall activity in the study.',
  },
  'dumbbell-fly': {
    regions: [
      { region: 'Mid chest', share: 50 },
      { region: 'Upper chest', share: 20 },
      { region: 'Lower chest', share: 15 },
      { region: 'Front delts', share: 15 },
    ],
    confidence: 'moderate',
    sources: ['solstad2020', 'campos2020'],
    note: 'With straight-ish arms the triceps barely work, so the fly is almost all chest with some front delt.',
  },
  'dumbbell-lateral-raise': {
    regions: [
      { region: 'Side delts', share: 50 },
      { region: 'Upper back and traps', share: 20 },
      { region: 'Front delts', share: 15 },
      { region: 'Rear delts', share: 15 },
    ],
    confidence: 'moderate',
    sources: ['coratella2020raise', 'campos2020'],
    note: 'The lateral raise works the side delts most, with rear delts and upper traps helping, especially near the top.',
  },
  'dumbbell-overhead-extension': {
    regions: [
      { region: 'Triceps long head', share: 50 },
      { region: 'Triceps lateral and medial heads', share: 50 },
    ],
    confidence: 'moderate',
    sources: ['maeo2023'],
    note: 'Arms overhead stretch the long head, which grew about 1.5 times more than with pushdowns.',
  },
  'dumbbell-romanian-deadlift': {
    regions: [
      { region: 'Hamstrings', share: 50 },
      { region: 'Glutes', share: 30 },
      { region: 'Lower back', share: 20 },
    ],
    confidence: 'moderate',
    sources: ['mcallister2014', 'martinfuentes2020'],
    note: 'Same hip hinge as the barbell RDL, so the same split applies.',
  },
  'ez-bar-curl': {
    regions: [
      { region: 'Biceps', share: 50 },
      { region: 'Brachialis', share: 30 },
      { region: 'Forearms (brachioradialis)', share: 20 },
    ],
    confidence: 'moderate',
    sources: ['marcolin2018'],
    note: 'The EZ curl matched the straight bar for biceps and showed slightly more brachioradialis activity.',
  },
  'face-pull': {
    regions: [
      { region: 'Upper back and traps', share: 45 },
      { region: 'Rear delts', share: 40 },
      { region: 'Side delts', share: 15 },
    ],
    confidence: 'low',
    sources: ['schoenfeld2013', 'mcallister2013'],
    note: 'Extrapolated from reverse fly and upright row data: pulling high toward the face shares the work between rear delts and upper back.',
  },
  'front-raise': {
    regions: [
      { region: 'Front delts', share: 65 },
      { region: 'Side delts', share: 15 },
      { region: 'Upper chest', share: 10 },
      { region: 'Upper back and traps', share: 10 },
    ],
    confidence: 'moderate',
    sources: ['coratella2020raise'],
    note: 'The front raise works the front delts the most of any raise and the side delts and traps the least.',
  },
  'front-squat': {
    regions: [
      { region: 'Quads', share: 55 },
      { region: 'Glutes', share: 20 },
      { region: 'Adductors', share: 15 },
      { region: 'Lower back', share: 10 },
    ],
    confidence: 'low',
    sources: ['yavuz2015', 'kubo2019'],
    note: 'A more upright torso shifts work toward the quads; the split is extrapolated from back squat growth data.',
  },
  'glute-bridge': {
    regions: [
      { region: 'Glutes', share: 60 },
      { region: 'Hamstrings', share: 25 },
      { region: 'Quads', share: 15 },
    ],
    confidence: 'low',
    sources: ['contreras2015', 'neto2020'],
    note: 'A shorter-range floor version of the hip thrust with more relative hamstring help; extrapolated from hip thrust data.',
  },
  'goblet-squat': {
    regions: [
      { region: 'Quads', share: 50 },
      { region: 'Glutes', share: 25 },
      { region: 'Adductors', share: 15 },
      { region: 'Core', share: 10 },
    ],
    confidence: 'low',
    sources: ['kubo2019', 'schoenfeld2010'],
    note: 'A front-loaded deep squat, extrapolated from back squat growth data with the front load adding core work.',
  },
  'good-morning': {
    regions: [
      { region: 'Lower back', share: 40 },
      { region: 'Hamstrings', share: 35 },
      { region: 'Glutes', share: 25 },
    ],
    confidence: 'moderate',
    sources: ['vigotsky2015'],
    note: 'Erector activity was clearly higher than hamstring activity; glutes were not measured, so their share is an estimate.',
  },
  'hack-squat': {
    regions: [
      { region: 'Quads', share: 65 },
      { region: 'Glutes', share: 20 },
      { region: 'Adductors', share: 15 },
    ],
    confidence: 'low',
    sources: ['escamilla2001', 'kubo2019'],
    note: 'A guided machine squat with the back supported, so quads dominate and the lower back does little; extrapolated from squat and leg press data.',
  },
  'hammer-curl': {
    regions: [
      { region: 'Biceps', share: 40 },
      { region: 'Brachialis', share: 35 },
      { region: 'Forearms (brachioradialis)', share: 25 },
    ],
    confidence: 'low',
    sources: ['coratella2023', 'kleiber2015'],
    note: 'A neutral grip lowers biceps activity relative to an underhand grip; the larger brachialis share is an anatomical estimate, not measured.',
  },
  'hanging-leg-raise': {
    regions: [
      { region: 'Lower abs', share: 30 },
      { region: 'Hip flexors', share: 25 },
      { region: 'Upper abs', share: 20 },
      { region: 'Obliques', share: 15 },
      { region: 'Grip', share: 10 },
    ],
    confidence: 'moderate',
    sources: ['escamilla2006'],
    note: 'Hanging knee-ups were among the top exercises for rectus abdominis and obliques, with heavy hip flexor involvement.',
  },
  'hip-abduction': {
    regions: [
      { region: 'Glute medius and minimus', share: 70 },
      { region: 'Glute maximus', share: 30 },
    ],
    confidence: 'low',
    sources: ['distefano2009'],
    note: 'Hip abduction mostly works the gluteus medius with upper glute max help; extrapolated from side-lying abduction EMG.',
  },
  'hip-adduction': {
    regions: [
      { region: 'Adductors', share: 90 },
      { region: 'Glutes', share: 10 },
    ],
    confidence: 'low',
    sources: ['serner2014'],
    note: 'The machine targets the inner thigh adductors almost entirely; based on adduction exercise EMG in soccer players.',
  },
  'hip-thrust': {
    regions: [
      { region: 'Glutes', share: 60 },
      { region: 'Quads', share: 25 },
      { region: 'Hamstrings', share: 15 },
    ],
    confidence: 'moderate',
    sources: ['contreras2015', 'neto2020'],
    note: 'The hip thrust showed much higher glute and hamstring activity than the squat, with the quads still working to hold the knee.',
  },
  'incline-barbell-bench-press': {
    regions: [
      { region: 'Upper chest', share: 40 },
      { region: 'Front delts', share: 25 },
      { region: 'Triceps', share: 20 },
      { region: 'Mid chest', share: 15 },
    ],
    confidence: 'moderate',
    sources: ['rodriguezRidao2020', 'lauver2016', 'coratella2020bench'],
    note: 'A 30 to 45 degree incline shifts work toward the upper chest (clavicular head) and the front delts, while mid chest activity drops.',
  },
  'incline-dumbbell-curl': {
    regions: [
      { region: 'Biceps', share: 60 },
      { region: 'Brachialis', share: 25 },
      { region: 'Forearms (brachioradialis)', share: 15 },
    ],
    confidence: 'moderate',
    sources: ['oliveira2009'],
    note: 'Incline curls reached biceps activity as high as standing curls while working the long head from a stretched shoulder position.',
  },
  'incline-dumbbell-press': {
    regions: [
      { region: 'Upper chest', share: 40 },
      { region: 'Front delts', share: 25 },
      { region: 'Mid chest', share: 20 },
      { region: 'Triceps', share: 15 },
    ],
    confidence: 'moderate',
    sources: ['rodriguezRidao2020', 'lauver2016', 'saeterbakken2011'],
    note: 'A 30 to 45 degree incline shifts work toward the upper chest (clavicular head), and dumbbells use a little less triceps than a barbell.',
  },
  'kettlebell-swing': {
    regions: [
      { region: 'Glutes', share: 45 },
      { region: 'Lower back', share: 25 },
      { region: 'Hamstrings', share: 20 },
      { region: 'Core', share: 10 },
    ],
    confidence: 'moderate',
    sources: ['mcgill2012'],
    note: 'Swings showed high glute activity, moderate back activity and less hamstring activity.',
  },
  'lat-pulldown': {
    regions: [
      { region: 'Lats', share: 50 },
      { region: 'Mid back', share: 20 },
      { region: 'Biceps', share: 20 },
      { region: 'Rear delts', share: 10 },
    ],
    confidence: 'moderate',
    sources: ['lusk2010', 'lehman2004'],
    note: 'An overhand pulldown activates the lats most, with mid traps and biceps contributing and grip width changing little.',
  },
  'leg-extension': {
    regions: [
      { region: 'Vasti (outer and inner quads)', share: 70 },
      { region: 'Rectus femoris', share: 30 },
    ],
    confidence: 'moderate',
    sources: ['ema2013'],
    note: 'An isolation lift for the quads; knee extension training grows the rectus femoris more than squats do, alongside the larger vasti.',
  },
  'leg-press': {
    regions: [
      { region: 'Quads', share: 65 },
      { region: 'Glutes', share: 20 },
      { region: 'Adductors', share: 15 },
    ],
    confidence: 'moderate',
    sources: ['escamilla2001', 'kubo2019'],
    note: 'Leg press drives the quads hard with less hamstring activity than the squat; glute and adductor shares follow squat growth data.',
  },
  'leg-press-calf-raise': {
    regions: [
      { region: 'Gastrocnemius', share: 60 },
      { region: 'Soleus', share: 40 },
    ],
    confidence: 'low',
    sources: ['kinoshita2023'],
    note: 'Knees stay straight as in the standing calf raise, so the same split is extrapolated.',
  },
  'lying-leg-curl': {
    regions: [
      { region: 'Hamstrings', share: 90 },
      { region: 'Calves', share: 10 },
    ],
    confidence: 'moderate',
    sources: ['maeo2021'],
    note: 'Nearly all hamstrings, with the gastrocnemius helping to bend the knee.',
  },
  'machine-chest-press': {
    regions: [
      { region: 'Mid chest', share: 40 },
      { region: 'Lower chest', share: 20 },
      { region: 'Front delts', share: 15 },
      { region: 'Triceps', share: 15 },
      { region: 'Upper chest', share: 10 },
    ],
    confidence: 'moderate',
    sources: ['coratella2020bench'],
    note: 'The machine works the mid chest as well as a flat bench but uses less upper chest and triceps.',
  },
  'machine-hip-thrust': {
    regions: [
      { region: 'Glutes', share: 60 },
      { region: 'Quads', share: 25 },
      { region: 'Hamstrings', share: 15 },
    ],
    confidence: 'low',
    sources: ['contreras2015'],
    note: 'Same movement as the barbell hip thrust on a machine, extrapolated from barbell data.',
  },
  'machine-lateral-raise': {
    regions: [
      { region: 'Side delts', share: 55 },
      { region: 'Upper back and traps', share: 20 },
      { region: 'Front delts', share: 15 },
      { region: 'Rear delts', share: 10 },
    ],
    confidence: 'low',
    sources: ['coratella2020raise'],
    note: 'Extrapolated from dumbbell lateral raise data; the pads guide the arms out to the side.',
  },
  'machine-row': {
    regions: [
      { region: 'Lats', share: 35 },
      { region: 'Mid back', share: 30 },
      { region: 'Biceps', share: 20 },
      { region: 'Rear delts', share: 15 },
    ],
    confidence: 'low',
    sources: ['vasconcelos2023'],
    note: 'Extrapolated from the seated cable row, which has the same supported horizontal pull.',
  },
  'machine-shoulder-press': {
    regions: [
      { region: 'Front delts', share: 45 },
      { region: 'Triceps', share: 30 },
      { region: 'Side delts', share: 15 },
      { region: 'Upper chest', share: 10 },
    ],
    confidence: 'moderate',
    sources: ['coratella2022ohp'],
    note: 'The machine works the delts less than a barbell press, especially the side delts, because the path is fixed.',
  },
  'nordic-curl': {
    regions: [
      { region: 'Hamstrings', share: 90 },
      { region: 'Calves', share: 10 },
    ],
    confidence: 'moderate',
    sources: ['bourne2017'],
    note: 'Nordic training mainly grew the semitendinosus, with the gastrocnemius assisting at the knee.',
  },
  'one-arm-dumbbell-row': {
    regions: [
      { region: 'Lats', share: 40 },
      { region: 'Mid back', share: 25 },
      { region: 'Biceps', share: 20 },
      { region: 'Rear delts', share: 10 },
      { region: 'Obliques', share: 5 },
    ],
    confidence: 'low',
    sources: ['fenwick2009', 'lehman2004'],
    note: 'Extrapolated from rowing studies; bracing on a bench removes most lower back load and the one-sided pull asks a little of the obliques.',
  },
  'overhead-press': {
    regions: [
      { region: 'Front delts', share: 40 },
      { region: 'Triceps', share: 25 },
      { region: 'Side delts', share: 20 },
      { region: 'Upper back and traps', share: 10 },
      { region: 'Upper chest', share: 5 },
    ],
    confidence: 'moderate',
    sources: ['coratella2022ohp', 'campos2020'],
    note: 'Pressing overhead in front of the head is led by the front delts, with side delts, triceps and a little upper chest helping.',
  },
  'overhead-triceps-extension': {
    regions: [
      { region: 'Triceps long head', share: 50 },
      { region: 'Triceps lateral and medial heads', share: 50 },
    ],
    confidence: 'moderate',
    sources: ['maeo2023'],
    note: 'Arms overhead stretch the long head, which grew about 1.5 times more than with pushdowns.',
  },
  'pallof-press': {
    regions: [
      { region: 'Obliques', share: 50 },
      { region: 'Deep core', share: 30 },
      { region: 'Upper abs', share: 10 },
      { region: 'Lower back', share: 10 },
    ],
    confidence: 'low',
    sources: ['stephens2021', 'page2012'],
    note: 'Evidence is a small pilot study; resisting rotation mainly loads the obliques and deep stabilisers.',
  },
  'pec-deck': {
    regions: [
      { region: 'Mid chest', share: 55 },
      { region: 'Upper chest', share: 15 },
      { region: 'Lower chest', share: 15 },
      { region: 'Front delts', share: 15 },
    ],
    confidence: 'low',
    sources: ['solstad2020'],
    note: 'Extrapolated from dumbbell fly data: the machine guides the arc, keeping the work on the chest.',
  },
  'pendlay-row': {
    regions: [
      { region: 'Mid back', share: 30 },
      { region: 'Lats', share: 25 },
      { region: 'Lower back', share: 20 },
      { region: 'Biceps', share: 15 },
      { region: 'Rear delts', share: 10 },
    ],
    confidence: 'low',
    sources: ['fenwick2009'],
    note: 'Extrapolated from the bent-over barbell row; restarting from the floor keeps the same muscles and back demand.',
  },
  'pike-push-up': {
    regions: [
      { region: 'Front delts', share: 45 },
      { region: 'Triceps', share: 25 },
      { region: 'Side delts', share: 15 },
      { region: 'Core', share: 10 },
      { region: 'Upper chest', share: 5 },
    ],
    confidence: 'low',
    sources: ['coratella2022ohp', 'calatayud2015'],
    note: 'Extrapolated from overhead press data: the pike angle turns a push-up into a bodyweight shoulder press.',
  },
  plank: {
    regions: [
      { region: 'Obliques', share: 40 },
      { region: 'Lower abs', share: 20 },
      { region: 'Upper abs', share: 20 },
      { region: 'Deep core', share: 20 },
    ],
    confidence: 'moderate',
    sources: ['schoenfeld2014'],
    note: 'In a standard plank the external obliques were most active, ahead of the lower abdominal stabilisers and upper rectus abdominis.',
  },
  'preacher-curl': {
    regions: [
      { region: 'Biceps', share: 50 },
      { region: 'Brachialis', share: 35 },
      { region: 'Forearms (brachioradialis)', share: 15 },
    ],
    confidence: 'moderate',
    sources: ['oliveira2009'],
    note: 'Preacher curls peak biceps activity early in the lift and drop off near the top; brachialis share is estimated.',
  },
  'pull-up': {
    regions: [
      { region: 'Lats', share: 45 },
      { region: 'Mid back', share: 20 },
      { region: 'Biceps', share: 15 },
      { region: 'Forearms (brachioradialis)', share: 10 },
      { region: 'Grip', share: 10 },
    ],
    confidence: 'moderate',
    sources: ['youdas2010'],
    note: 'Lats are similar in pull-ups and chin-ups, while the overhand grip brings more lower trapezius and less biceps than a chin-up.',
  },
  'push-up': {
    regions: [
      { region: 'Mid chest', share: 40 },
      { region: 'Triceps', share: 20 },
      { region: 'Lower chest', share: 15 },
      { region: 'Front delts', share: 15 },
      { region: 'Core', share: 10 },
    ],
    confidence: 'moderate',
    sources: ['calatayud2015'],
    note: 'Push-ups work the same muscles as a flat bench press at a matched effort, plus the core to hold a plank.',
  },
  'rear-delt-fly': {
    regions: [
      { region: 'Rear delts', share: 50 },
      { region: 'Upper back and traps', share: 35 },
      { region: 'Side delts', share: 15 },
    ],
    confidence: 'low',
    sources: ['schoenfeld2013'],
    note: 'Extrapolated from reverse fly machine data: pulling the arms out to the side works the rear delts with help from the upper back.',
  },
  'reverse-pec-deck': {
    regions: [
      { region: 'Rear delts', share: 50 },
      { region: 'Upper back and traps', share: 35 },
      { region: 'Side delts', share: 15 },
    ],
    confidence: 'moderate',
    sources: ['schoenfeld2013'],
    note: 'The reverse fly machine works the rear delts and upper back; a neutral grip raised rear delt activity.',
  },
  'romanian-deadlift': {
    regions: [
      { region: 'Hamstrings', share: 50 },
      { region: 'Glutes', share: 30 },
      { region: 'Lower back', share: 20 },
    ],
    confidence: 'moderate',
    sources: ['mcallister2014', 'martinfuentes2020'],
    note: 'The RDL shows high hamstring activity and lower erector activity than the hamstrings.',
  },
  'seated-cable-row': {
    regions: [
      { region: 'Lats', share: 35 },
      { region: 'Mid back', share: 30 },
      { region: 'Biceps', share: 20 },
      { region: 'Rear delts', share: 15 },
    ],
    confidence: 'moderate',
    sources: ['vasconcelos2023', 'lehman2004'],
    note: 'Close grips favour the lats while wider, elbows-out grips shift work to the mid traps and rear delts.',
  },
  'seated-calf-raise': {
    regions: [
      { region: 'Soleus', share: 85 },
      { region: 'Gastrocnemius', share: 15 },
    ],
    confidence: 'moderate',
    sources: ['kinoshita2023'],
    note: 'Bent knees take the gastrocnemius out of the movement, so the soleus does most of the work.',
  },
  'seated-dumbbell-press': {
    regions: [
      { region: 'Front delts', share: 40 },
      { region: 'Side delts', share: 25 },
      { region: 'Triceps', share: 25 },
      { region: 'Upper back and traps', share: 10 },
    ],
    confidence: 'low',
    sources: ['campos2020', 'coratella2022ohp'],
    note: 'Extrapolated from barbell shoulder press data; dumbbells let the elbows flare a little, which adds some side delt.',
  },
  'seated-leg-curl': {
    regions: [
      { region: 'Hamstrings', share: 95 },
      { region: 'Calves', share: 5 },
    ],
    confidence: 'moderate',
    sources: ['maeo2021'],
    note: 'Training the hamstrings at a longer length grew them more than the lying curl; the calves help only a little.',
  },
  'side-plank': {
    regions: [
      { region: 'Obliques', share: 55 },
      { region: 'Deep core', share: 25 },
      { region: 'Glutes', share: 10 },
      { region: 'Lower back', share: 10 },
    ],
    confidence: 'moderate',
    sources: ['page2012'],
    note: 'The side hold recruits many trunk muscles, with the obliques working hardest and fatiguing fastest.',
  },
  'single-leg-calf-raise': {
    regions: [
      { region: 'Gastrocnemius', share: 60 },
      { region: 'Soleus', share: 40 },
    ],
    confidence: 'low',
    sources: ['kinoshita2023'],
    note: 'A straight-knee calf raise on one leg, extrapolated from the standing calf raise study.',
  },
  'skull-crusher': {
    regions: [
      { region: 'Triceps lateral and medial heads', share: 60 },
      { region: 'Triceps long head', share: 40 },
    ],
    confidence: 'low',
    sources: ['maeo2023'],
    note: 'Extrapolated from overhead and pushdown data: arms halfway up put the long head between the two.',
  },
  'smith-machine-bench-press': {
    regions: [
      { region: 'Mid chest', share: 35 },
      { region: 'Triceps', share: 20 },
      { region: 'Lower chest', share: 15 },
      { region: 'Upper chest', share: 15 },
      { region: 'Front delts', share: 15 },
    ],
    confidence: 'moderate',
    sources: ['saeterbakken2011', 'rodriguezRidao2020'],
    note: 'Chest and front delt activity on the Smith machine matched the free barbell bench press.',
  },
  'standing-calf-raise': {
    regions: [
      { region: 'Gastrocnemius', share: 60 },
      { region: 'Soleus', share: 40 },
    ],
    confidence: 'moderate',
    sources: ['kinoshita2023'],
    note: 'With straight knees the gastrocnemius grew far more than the soleus.',
  },
  'step-up': {
    regions: [
      { region: 'Quads', share: 50 },
      { region: 'Glutes', share: 40 },
      { region: 'Hamstrings', share: 10 },
    ],
    confidence: 'moderate',
    sources: ['neto2020', 'muyor2020'],
    note: 'Step-ups produced some of the highest glute activation in the review while the quads drive the knee.',
  },
  'stiff-leg-deadlift': {
    regions: [
      { region: 'Hamstrings', share: 45 },
      { region: 'Lower back', share: 30 },
      { region: 'Glutes', share: 25 },
    ],
    confidence: 'moderate',
    sources: ['martinfuentes2020'],
    note: 'With straighter knees the hamstrings work hard and the erectors showed the highest activation in the studies reviewed.',
  },
  'straight-arm-pulldown': {
    regions: [
      { region: 'Lats', share: 70 },
      { region: 'Mid back', share: 10 },
      { region: 'Rear delts', share: 10 },
      { region: 'Deep core', share: 10 },
    ],
    confidence: 'moderate',
    sources: ['muyor2022'],
    note: 'Lats were most active in the straight-arm pulldown; the triceps long head also works but is not a tracked region.',
  },
  'sumo-deadlift': {
    regions: [
      { region: 'Quads', share: 30 },
      { region: 'Glutes', share: 30 },
      { region: 'Adductors', share: 15 },
      { region: 'Lower back', share: 15 },
      { region: 'Hamstrings', share: 10 },
    ],
    confidence: 'moderate',
    sources: ['escamilla2002', 'martinfuentes2020'],
    note: 'The wide stance raised quad activity compared with the conventional deadlift, with similar back and hamstring activity.',
  },
  't-bar-row': {
    regions: [
      { region: 'Mid back', share: 30 },
      { region: 'Lats', share: 30 },
      { region: 'Lower back', share: 15 },
      { region: 'Biceps', share: 15 },
      { region: 'Rear delts', share: 10 },
    ],
    confidence: 'low',
    sources: ['fenwick2009'],
    note: 'Extrapolated from the bent-over barbell row; the hinged position still loads the lower back, a bit less with a close grip.',
  },
  'triceps-bar-pushdown': {
    regions: [
      { region: 'Triceps lateral and medial heads', share: 65 },
      { region: 'Triceps long head', share: 35 },
    ],
    confidence: 'moderate',
    sources: ['maeo2023'],
    note: 'With the arms at your sides the long head is shortened, so pushdowns grow it less than overhead work.',
  },
  'triceps-rope-pushdown': {
    regions: [
      { region: 'Triceps lateral and medial heads', share: 65 },
      { region: 'Triceps long head', share: 35 },
    ],
    confidence: 'moderate',
    sources: ['maeo2023'],
    note: 'With the arms at your sides the long head is shortened, so pushdowns grow it less than overhead work.',
  },
  'upright-row': {
    regions: [
      { region: 'Side delts', share: 40 },
      { region: 'Upper back and traps', share: 35 },
      { region: 'Front delts', share: 15 },
      { region: 'Rear delts', share: 10 },
    ],
    confidence: 'moderate',
    sources: ['mcallister2013'],
    note: 'The upright row works the side delts and traps together; a wider grip raises both.',
  },
  'walking-lunge': {
    regions: [
      { region: 'Quads', share: 45 },
      { region: 'Glutes', share: 35 },
      { region: 'Adductors', share: 10 },
      { region: 'Hamstrings', share: 10 },
    ],
    confidence: 'moderate',
    sources: ['muyor2020', 'neto2020'],
    note: 'Forward lunges show high quad and glute activity with some hamstring involvement.',
  },
  'weighted-dips': {
    regions: [
      { region: 'Lower chest', share: 35 },
      { region: 'Triceps', share: 30 },
      { region: 'Front delts', share: 15 },
      { region: 'Mid chest', share: 10 },
      { region: 'Serratus', share: 10 },
    ],
    confidence: 'moderate',
    sources: ['mckenzie2022dip'],
    note: 'Same pattern as bodyweight dips; added load raises effort but not the split.',
  },
  'weighted-pull-up': {
    regions: [
      { region: 'Lats', share: 45 },
      { region: 'Mid back', share: 20 },
      { region: 'Biceps', share: 15 },
      { region: 'Forearms (brachioradialis)', share: 10 },
      { region: 'Grip', share: 10 },
    ],
    confidence: 'moderate',
    sources: ['youdas2010'],
    note: 'Same movement as the pull-up with added load, so the same split applies.',
  },
  'kelso-shrug': {
    regions: [
      { region: 'Mid back', share: 55 },
      { region: 'Upper back and traps', share: 30 },
      { region: 'Rear delts', share: 10 },
      { region: 'Grip', share: 5 },
    ],
    confidence: 'low',
    sources: ['pizzari2014'],
    note: 'Not measured as such: an estimate from what pulling the shoulder blades back works (middle and lower trapezius, rhomboids). The chest support and straight arms leave the lats and biceps little to do.',
  },
  'keenan-flap': {
    regions: [
      { region: 'Lats', share: 75 },
      { region: 'Mid back', share: 15 },
      { region: 'Rear delts', share: 10 },
    ],
    confidence: 'low',
    sources: ['muyor2022'],
    note: 'Not measured as such: estimated from the straight-arm pulldown, the closest studied exercise. Pulling the arm down to the side is the lats’ main job; the teres major helps.',
  },
  'behind-the-neck-press': {
    regions: [
      { region: 'Front delts', share: 30 },
      { region: 'Side delts', share: 25 },
      { region: 'Triceps', share: 25 },
      { region: 'Rear delts', share: 10 },
      { region: 'Upper back and traps', share: 10 },
    ],
    confidence: 'moderate',
    sources: ['coratella2022ohp', 'padovan2025'],
    note: 'Compared with pressing in front, pressing from behind the head works the front delts and chest less and the rear delts, upper traps and triceps more.',
  },
  'brachioradialis-curl': {
    regions: [
      { region: 'Forearms (brachioradialis)', share: 40 },
      { region: 'Brachialis', share: 35 },
      { region: 'Biceps', share: 25 },
    ],
    confidence: 'low',
    sources: ['kleiber2015', 'marcolin2018'],
    note: 'Turning the palms down takes work off the biceps and onto the brachioradialis and brachialis; the shares are an estimate from grip-position studies.',
  },
  'dumbbell-stiff-leg-deadlift': {
    regions: [
      { region: 'Hamstrings', share: 45 },
      { region: 'Lower back', share: 30 },
      { region: 'Glutes', share: 25 },
    ],
    confidence: 'moderate',
    sources: ['martinfuentes2020'],
    note: 'The same movement as the barbell stiff-leg deadlift with the load in the hands: hamstrings and spinal erectors work hardest, the glutes help.',
  },
  'box-squat': {
    regions: [
      { region: 'Quads', share: 45 },
      { region: 'Glutes', share: 25 },
      { region: 'Adductors', share: 20 },
      { region: 'Lower back', share: 10 },
    ],
    confidence: 'moderate',
    sources: ['mcbride2010', 'kubo2019'],
    note: 'Sitting briefly on a box changed muscle activity very little compared with a back squat to the same depth, so the shares follow the squat.',
  },
  'one-arm-cable-triceps-extension': {
    regions: [
      { region: 'Triceps lateral and medial heads', share: 65 },
      { region: 'Triceps long head', share: 35 },
    ],
    confidence: 'moderate',
    sources: ['maeo2023'],
    note: 'The same muscles as the two-arm pushdown, one arm at a time; taken overhead, the long head works at a longer length and grows more.',
  },
  'one-arm-machine-lateral-raise': {
    regions: [
      { region: 'Side delts', share: 55 },
      { region: 'Upper back and traps', share: 20 },
      { region: 'Front delts', share: 15 },
      { region: 'Rear delts', share: 10 },
    ],
    confidence: 'low',
    sources: ['coratella2020raise'],
    note: 'The same muscles as the two-arm machine raise, one side at a time.',
  },
  'single-leg-extension': {
    regions: [
      { region: 'Vasti (outer and inner quads)', share: 70 },
      { region: 'Rectus femoris', share: 30 },
    ],
    confidence: 'moderate',
    sources: ['ema2013'],
    note: 'The same muscles as the two-leg extension, one leg at a time.',
  },
  'one-arm-pec-deck': {
    regions: [
      { region: 'Mid chest', share: 55 },
      { region: 'Upper chest', share: 15 },
      { region: 'Lower chest', share: 15 },
      { region: 'Front delts', share: 15 },
    ],
    confidence: 'low',
    sources: ['solstad2020'],
    note: 'The same muscles as the two-arm pec deck, one side at a time.',
  },
  'single-leg-press': {
    regions: [
      { region: 'Quads', share: 65 },
      { region: 'Glutes', share: 20 },
      { region: 'Adductors', share: 15 },
    ],
    confidence: 'low',
    sources: ['escamilla2001', 'kubo2019'],
    note: 'Estimated from the two-leg press; on one leg the glutes also work to keep the hip steady.',
  },
  'single-leg-curl': {
    regions: [
      { region: 'Hamstrings', share: 95 },
      { region: 'Calves', share: 5 },
    ],
    confidence: 'moderate',
    sources: ['maeo2021'],
    note: 'The same muscles as the two-leg curl, one leg at a time.',
  },
  'one-arm-lat-pulldown': {
    regions: [
      { region: 'Lats', share: 50 },
      { region: 'Mid back', share: 20 },
      { region: 'Biceps', share: 20 },
      { region: 'Rear delts', share: 10 },
    ],
    confidence: 'low',
    sources: ['lusk2010', 'lehman2004'],
    note: 'Estimated from the two-arm pulldown, one arm at a time.',
  },
  'one-arm-cable-row': {
    regions: [
      { region: 'Lats', share: 35 },
      { region: 'Mid back', share: 30 },
      { region: 'Biceps', share: 20 },
      { region: 'Rear delts', share: 15 },
    ],
    confidence: 'low',
    sources: ['vasconcelos2023', 'lehman2004'],
    note: 'Estimated from the seated cable row, one arm at a time.',
  },
  'one-arm-cable-curl': {
    regions: [
      { region: 'Biceps', share: 55 },
      { region: 'Brachialis', share: 30 },
      { region: 'Forearms (brachioradialis)', share: 15 },
    ],
    confidence: 'moderate',
    sources: ['coratella2023'],
    note: 'The same muscles as the two-arm cable curl, one arm at a time.',
  },
  'single-leg-romanian-deadlift': {
    regions: [
      { region: 'Hamstrings', share: 45 },
      { region: 'Glutes', share: 35 },
      { region: 'Lower back', share: 20 },
    ],
    confidence: 'low',
    sources: ['mcallister2014', 'martinfuentes2020'],
    note: 'Estimated from the Romanian deadlift; balancing on one leg asks more of the glutes.',
  },
};
